import Close from "@mui/icons-material/Close";
import ControlPoint from "@mui/icons-material/ControlPoint";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type FC, type ReactNode, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useToken } from "../../server/auth/use-token";
import { ImagesService } from "../../server/cloudflare/images-service";
import { MediaService } from "../../server/cloudflare/media-service";
import { NameSpaceURL, type UUID, uuidv4, uuidv5 } from "../../utils/uuid";
import { FilePicker } from "../generic/components/file-picker/file-picker";
import { AssetFolderSelect } from "./asset-folder-select";
import { AssetTagManager } from "./asset-tag-manager";
import { VideoUidService } from "./client/video-uid-service";
import { useAvailableTags } from "./hooks/use-available-tags";
import type { AssetResult } from "./hooks/use-upsert-asset";
import type { Asset } from "./server/digital-asset-service";
import { UploadFileFailedToast } from "./upload-file-failed-toast";
import { constructCloudflareStreamUrl, ROOT_ID, type UiFolderId } from "./utils";

export const UploadFileType = {
  image: [".png", ".gif", ".jpg", ".jpeg"],
  video: [".mp4", ".mov"],
  pdf: [".pdf"],
};

export type CreateAssetResult = { success: true; id: UUID } | { success: false };

export type FileUploadProps = {
  open: boolean;
  heading: string;
  allowedFileTypes?: (keyof typeof UploadFileType)[];
  onClose: () => void;
  onCancel: () => void;
  onOpen: () => void;
  onChange?: (folderId: UiFolderId | null) => void;
  onSave: (asset: {
    id?: UUID;
    title: string;
    thumbnailUrl?: string | null;
    contentType: string;
    projectId?: string | null;
    pageId?: string | null;
    mimeType?: string | null;
    sourceDigitalAssetId?: UUID | null;
    publicUrl?: string | null;
    externalStorageId?: string | null;
    tags?: string[] | null;
    folderId?: UUID | null;
  }) => Promise<AssetResult>;
  helperText?: string;
  assetToReplaceId?: UUID;
  folderId?: UUID | null;
};

interface UploadState {
  fileUrl: string | null;
  fileTitle: string | null;
  fileType: string | null;
  showNewFolderField: boolean;
}

export const FileUpload: FC<
  FileUploadProps & {
    renderActions: (actions: {
      uploadState: UploadState;
      handleSave: (url?: string) => Promise<CreateAssetResult>;
      handleClose: () => void;
      asset: {
        public_url: string;
        thumbnail_url: string;
        mime_type?: string | null;
      } | null;
    }) => ReactNode;
    onSuccess: (fileTitle: string, asset: Asset) => void;
  }
> = ({
  open,
  heading,
  allowedFileTypes = ["pdf", "image"],
  onClose,
  onCancel,
  onOpen,
  onChange,
  onSave,
  renderActions,
  onSuccess,
  helperText,
  assetToReplaceId,
  folderId,
}) => {
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [cloudflareStreamUid, setCloudflareStreamUid] = useState<string | null>(null);
  const [fileTitle, setFileTitle] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [showFileTypeError, setShowFileTypeError] = useState<boolean>(false);
  const [fileTitleError, setFileTitleError] = useState<string | null>(null);
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [localFolderId, setLocalFolderId] = useState<UiFolderId | null>(null);
  const [showNewFolderField, setShowNewFolderField] = useState<boolean>(false);
  const getToken = useToken();
  const { t } = useTranslation();
  const { availableTags } = useAvailableTags();
  const selectedFolderId = localFolderId ?? folderId ?? ROOT_ID;
  const supportedFormats = [
    ...(allowedFileTypes.includes("pdf") ? ["pdf"] : []),
    ...(allowedFileTypes.includes("image") ? ["png", "gif", "jpg"] : []),
    ...(allowedFileTypes.includes("video") ? ["mp4", "mov"] : []),
  ].join(", ");

  const handleCancel = useCallback((): void => {
    setCurrentValue(null);
    setFileTitle(null);
    setFileTitleError(null);
    setLocalTags([]);
    setLocalFolderId(null);
    setShowNewFolderField(false);

    onCancel();
    setShowFileTypeError(false);
  }, [onCancel]);

  const handleSave = useCallback(
    async (url?: string): Promise<CreateAssetResult> => {
      if (!currentValue || !fileTitle) return { success: false };

      const imagesService = new ImagesService();
      const mediaService = new MediaService();

      let uploadUrl = url || currentValue;
      let thumbnailUrl;
      let assetId;

      if (fileType?.startsWith("image/")) {
        thumbnailUrl = uploadUrl;
      } else if (fileType?.startsWith("video/") && cloudflareStreamUid) {
        const videoUidService = new VideoUidService(await getToken());
        const { data, error } = await videoUidService.upsert(cloudflareStreamUid, assetToReplaceId);

        if (error || !data) {
          toast.error(error);
          return { success: false };
        }

        assetId = data.id;
        uploadUrl = mediaService.constructLandingPageURL(assetId);
        thumbnailUrl = imagesService.getCloudflareStreamThumbnailUrl(cloudflareStreamUid);
      }

      const { success, asset, error } = await onSave({
        id: assetId,
        title: fileTitle,
        mimeType: fileType,
        publicUrl: uploadUrl,
        contentType: "Uploaded File",
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        externalStorageId: cloudflareStreamUid,
        tags: localTags,
        folderId: selectedFolderId === ROOT_ID ? null : selectedFolderId,
      });

      if (!success) {
        if (error?.key === "name") {
          setFileTitleError(error.message);
        } else {
          toast.error(error ? error.message : "Failed to upload file. Please try again");
        }
        return { success: false };
      }

      setLocalTags([]);
      onSuccess(fileTitle, asset);
      return { success: true, id: asset.id };
    },
    [
      currentValue,
      fileTitle,
      fileType,
      cloudflareStreamUid,
      onSave,
      localTags,
      onSuccess,
      getToken,
      assetToReplaceId,
      selectedFolderId,
    ],
  );

  const handleClose = useCallback((): void => {
    onChange?.(localFolderId);
    setCurrentValue(null);
    setFileTitle(null);
    setCloudflareStreamUid(null);
    setLocalTags([]);
    setLocalFolderId(null);
    setShowNewFolderField(false);

    onClose();
    setShowFileTypeError(false);
  }, [localFolderId, onChange, onClose]);

  const handleNonVideoUpload = useCallback(
    async (file: File) => {
      setShowFileTypeError(false);

      const name = `${uuidv5(NameSpaceURL, uuidv4())}${file.type === "image/gif" ? ".gif" : ""}`;
      const response = await new ImagesService().upload(file, name, await getToken(), file.type);

      if (!response.success) {
        handleCancel();
        toast.custom(
          <UploadFileFailedToast
            filename={file.name}
            onClick={() => {
              toast.remove();
              onOpen();
            }}
          />,
        );

        console.error("Upload failed");
        return Promise.reject(new Error("Upload failed"));
      }

      setFileTitle(file.name);
      setFileType(file.type);

      setCurrentValue(response.url);
      return Promise.resolve(file);
    },
    [getToken, handleCancel, onOpen],
  );

  const handleVideoUpload = useCallback(
    async (file: File) => {
      setShowFileTypeError(false);
      const token = await getToken();

      const generateURLResponse = await new ImagesService().generateDirectUploadURL(token);
      if (!generateURLResponse.success) {
        toast.custom(
          <UploadFileFailedToast
            filename={file.name}
            onClick={() => {
              toast.remove();
              onOpen();
            }}
          />,
        );

        console.error("Upload failed");
        return Promise.reject(new Error("Upload failed"));
      }

      const formData = new FormData();
      formData.append("file", file);
      const uploadVideoResponse = await fetch(generateURLResponse.data.uploadURL, {
        method: "POST",
        body: formData,
      });

      if (!uploadVideoResponse.ok) {
        toast.custom(
          <UploadFileFailedToast
            filename={file.name}
            onClick={() => {
              toast.remove();
              onOpen();
            }}
          />,
        );
      }

      setFileTitle(file.name);
      setFileType(file.type);

      setCurrentValue(`${constructCloudflareStreamUrl(generateURLResponse.data.uid)}`);
      setCloudflareStreamUid(generateURLResponse.data.uid);
      return Promise.resolve(file);
    },
    [getToken, onOpen],
  );

  const handleUpload = useCallback(
    async (file: File) => {
      if (file.type.startsWith("video/")) {
        return handleVideoUpload(file);
      }

      return handleNonVideoUpload(file);
    },
    [handleNonVideoUpload, handleVideoUpload],
  );

  const handleRemoveUpload = useCallback(() => {
    setFileTitle(null);
    setFileType(null);
    setCurrentValue(null);
  }, []);

  const handleSetFileTitle = useCallback((newTitle: string) => {
    setFileTitle(newTitle);
    setFileTitleError(null);
  }, []);

  return (
    <Dialog open={open} onClose={handleCancel} slotProps={{ paper: { sx: { width: "600px" } } }}>
      <DialogTitle display="flex" justifyContent="space-between" component="div">
        <Typography variant="h6" flexGrow={1} mt={1}>
          {heading}
        </Typography>
        <IconButton
          onClick={handleCancel}
          data-analytics-id="upload-file-modal-close"
          title="Close"
        >
          <Close data-testid="CloseIcon" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            {t(
              "Please note that files uploaded to the Content Library will be accessible by anyone with a link to the file.",
            )}
          </Typography>
          <FilePicker
            value={currentValue}
            handleUpload={handleUpload}
            accept={{
              ...(allowedFileTypes.includes("image") ? { "image/*": UploadFileType.image } : {}),
              ...(allowedFileTypes.includes("video") ? { "video/*": UploadFileType.video } : {}),
              ...(allowedFileTypes.includes("pdf")
                ? { "application/pdf": UploadFileType.pdf }
                : {}),
            }}
            text={{
              remove: "Remove file",
              uploadError: "Only one file can be uploaded at a time",
            }}
            content={
              <Box sx={{ p: 2 }}>
                <Typography variant="h1" sx={{ textAlign: "center" }}>
                  <ControlPoint color="primary" fontSize="large" />
                </Typography>
                <Typography variant="body1" sx={{ textAlign: "center" }}>
                  Drag and drop a file here, or upload from your computer
                </Typography>
                <Typography variant="body2" sx={{ textAlign: "center" }} color="text.secondary">
                  {t("Supported formats: {{supportedFormats}}", { supportedFormats })}
                </Typography>
              </Box>
            }
            handleRemove={handleRemoveUpload}
            fileTypeError={() => {
              setShowFileTypeError(true);
            }}
          />
          {showFileTypeError && (
            <Alert severity="error">
              {t(
                "This file type is not currently supported. Supported formats: {{supportedFormats}}",
                { supportedFormats },
              )}
            </Alert>
          )}
          <TextField
            value={fileTitle ?? ""}
            id="file-title"
            placeholder="Title"
            onChange={(e) => handleSetFileTitle(e.target.value)}
            error={Boolean(fileTitleError)}
            helperText={fileTitleError}
          ></TextField>
          {helperText && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {helperText}
              </Typography>
            </Box>
          )}
          <Box sx={{ pb: 2 }}>
            <AssetFolderSelect
              selectedFolderId={selectedFolderId}
              onChange={(folderIdValue) => setLocalFolderId(folderIdValue)}
              onShowNewFolderFieldChange={setShowNewFolderField}
            />
          </Box>
          <AssetTagManager
            localTags={localTags}
            setLocalTags={setLocalTags}
            availableTags={availableTags}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyItems: "right", px: 2, mb: 2 }}>
        <Button onClick={handleCancel} data-analytics-id="upload-file-modal-cancel">
          Cancel
        </Button>
        {renderActions({
          uploadState: {
            fileUrl: currentValue,
            fileTitle,
            fileType,
            showNewFolderField,
          },
          handleSave,
          handleClose,
          asset: currentValue
            ? {
                public_url: currentValue,
                thumbnail_url: currentValue,
                mime_type: fileType,
              }
            : null,
        })}
      </DialogActions>
    </Dialog>
  );
};
